/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useBookingActive } from '@/contexts/SiteSettingsContext';
import { Calendar, Heart, Stethoscope, Baby, Eye, Droplets, Shield, Users, ArrowRight, MapPin, Phone, Clock, Tag, CalendarClock } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useSiteImage } from '@/contexts/SiteImagesContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPECIALTIES = [
  { icon: Heart, name: 'Ginecologia e Ostetricia', desc: 'Visite, ecografie, pap test e monitoraggio gravidanza', color: 'text-pink-600 bg-pink-50' },
  { icon: Droplets, name: 'Allergologia', desc: 'Test allergici e terapie desensibilizzanti per adulti e bambini', color: 'text-amber-600 bg-amber-50' },
  { icon: Eye, name: 'Diagnostica per Immagini', desc: 'Eco Color Doppler, ecografie addominali e tiroidee', color: 'text-blue-600 bg-blue-50' },
  { icon: Shield, name: 'Dermatologia', desc: 'Visite dermatologiche, dermatoscopia e mappatura nei', color: 'text-green-600 bg-green-50' },
  { icon: Stethoscope, name: 'Endocrinologia', desc: 'Patologie tiroidee, diabete e disturbi metabolici', color: 'text-purple-600 bg-purple-50' },
  { icon: Baby, name: 'Pediatria e Vaccinazioni', desc: 'Visite pediatriche, bilanci di salute e vaccinazioni', color: 'text-cyan-600 bg-cyan-50' },
];

export default function Home() {
  const [doctors, setDoctors] = useState([]);
  const bookingActive = useBookingActive();
  const [homeServices, setHomeServices] = useState([]);
  const [promos, setPromos] = useState([]);
  const [stats, setStats] = useState({ doctors: 0, services: 0 });
  const heroImg = useSiteImage('hero_homepage');
  const placeholderM = useSiteImage('placeholder_male');

  const FEATURED_IDS = [
    "53e4e09d-3c62-4ab3-9907-d607b1eeeabc", // Laura Spinaci
    "b94b7505-a1d2-4a11-a71b-ced67bb29e81", // Giorgio Gottardi
    "3e506cbf-4be7-4ae9-8966-479c294a0277", // Antonio Spreafico
    "92615ca2-d8e7-4402-b7da-2e45a832745d", // Lucia Calò
  ];

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/doctors`),
      axios.get(`${API}/services`),
      axios.get(`${API}/services?promo_only=true`).catch(() => ({ data: [] }))
    ]).then(([dRes, sRes, pRes]) => {
      const allDoctors = dRes.data;
      const featured = FEATURED_IDS.map(id => allDoctors.find(d => d.id === id)).filter(Boolean);
      setDoctors(featured);
      setHomeServices(sRes.data.filter(s => s.home_page));
      setPromos(pRes.data.filter(s => s.promo_attiva));
      setStats({ doctors: allDoctors.length, services: sRes.data.length });
    }).catch(() => {});
  }, []);

  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div>
      {/* Hero */}
      <section className="hero-wash py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]">Studio Medico Associato dal 1990</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1F2A37] leading-tight mb-6" style={{fontFamily:'Space Grotesk'}}>
                La salute è al centro del nostro <span className="text-[#238A92]">interesse</span>
              </h1>
              <p className="text-lg text-[hsl(var(--muted-foreground))] mb-8 leading-relaxed">
                Uno studio medico dove lavorano diversi specialisti, offrendo una gamma di servizi sempre più vasta. Prenota il tuo appuntamento in pochi clic.
              </p>
              <div className="flex flex-wrap gap-3">
                {bookingActive && (
                  <Link to="/prenota">
                    <Button size="lg" className="bg-[#238A92] hover:bg-[#0F5E66] text-white px-8" data-testid="hero-book-button">
                      <Calendar size={18} className="mr-2" /> Prenota Appuntamento
                    </Button>
                  </Link>
                )}
                <Link to="/servizi">
                  <Button size="lg" variant="outline" className="border-[#238A92] text-[#238A92] hover:bg-[#238A92] hover:text-white">
                    I Nostri Servizi <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              {heroImg && <img src={heroImg} alt="Studio Medico Boscovich" className="rounded-2xl shadow-[var(--shadow-lg)] object-cover w-full h-[400px]" />}
            </div>
          </div>
        </div>
      </section>

      {/* Trust badges */}
      {/* Promozioni */}
      {promos.length > 0 && (
        <section className="py-12 sm:py-16 bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]" data-testid="home-promo-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
              <div>
                <Badge className="mb-3 bg-[#238A92] text-white hover:bg-[#238A92]"><Tag size={12} className="mr-1" /> Offerte a tempo limitato</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2A37]" style={{fontFamily:'Space Grotesk'}}>Promozioni</h2>
                <p className="text-muted-foreground mt-2 max-w-2xl">Prestazioni selezionate a un prezzo dedicato. Prenota subito online, prima della scadenza.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 items-stretch auto-rows-fr" data-testid="home-promo-grid">
              {promos.map((service) => (
                <Card key={service.id} className="h-full overflow-hidden flex flex-col border-[rgba(35,138,146,.25)] hover:shadow-[var(--shadow-md)] hover:border-[rgba(35,138,146,.5)] transition-shadow transition-colors duration-200" data-testid={`home-promo-card-${service.id}`}>
                  {service.immagine_url && (
                    <div className="relative h-40 overflow-hidden bg-white">
                      <img src={service.immagine_url} alt={service.nome} className="w-full h-full object-cover" loading="lazy" />
                      <Badge className="absolute top-3 left-3 bg-[#238A92] text-white hover:bg-[#238A92] shadow">Promo</Badge>
                    </div>
                  )}
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {!service.immagine_url && <Badge className="bg-[#238A92] text-white hover:bg-[#238A92] text-[11px]">Promo</Badge>}
                      <Badge variant="secondary" className="w-fit text-[11px]">{service.categoria}</Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>{service.nome}</h3>
                    {service.descrizione && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-4">{service.descrizione}</p>}
                    <div className="mt-auto space-y-3">
                      <div className="flex items-end justify-between gap-2">
                        <div>
                          {service.prezzo != null && (
                            <p className="text-2xl font-bold text-[#238A92]" style={{fontFamily:'Space Grotesk'}} data-testid={`home-promo-price-${service.id}`}>{Number(service.prezzo).toFixed(2)} €</p>
                          )}
                          <p className="text-xs text-muted-foreground inline-flex items-center gap-1"><Clock size={12} /> {service.durata_minuti} min</p>
                        </div>
                        {service.promo_scadenza && (
                          <p className="text-xs text-muted-foreground inline-flex items-center gap-1 text-right" data-testid={`home-promo-scadenza-${service.id}`}><CalendarClock size={12} /> Fino al {formatDate(service.promo_scadenza)}</p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/servizi/${service.id}`} className="flex-1">
                          <Button variant="outline" className="w-full border-[#238A92] text-[#238A92] hover:bg-[#238A92] hover:text-white" data-testid={`home-promo-detail-btn-${service.id}`}>Dettagli</Button>
                        </Link>
                        {bookingActive && (
                          <Link to={`/prenota?service=${service.id}`} className="flex-1">
                            <Button className="w-full bg-[#238A92] hover:bg-[#0F5E66] text-white" data-testid={`home-promo-book-btn-${service.id}`}><Calendar size={15} className="mr-1" /> Prenota</Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="border-b border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div><p className="text-2xl font-bold text-[#238A92]" style={{fontFamily:'Space Grotesk'}}>30+</p><p className="text-sm text-muted-foreground">Anni di esperienza</p></div>
            <div><p className="text-2xl font-bold text-[#238A92]" style={{fontFamily:'Space Grotesk'}}>{stats.doctors}</p><p className="text-sm text-muted-foreground">Specialisti</p></div>
            <div><p className="text-2xl font-bold text-[#238A92]" style={{fontFamily:'Space Grotesk'}}>{stats.services}</p><p className="text-sm text-muted-foreground">Specialità</p></div>
            <div><p className="text-2xl font-bold text-[#238A92]" style={{fontFamily:'Space Grotesk'}}>5000+</p><p className="text-sm text-muted-foreground">Pazienti soddisfatti</p></div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2A37] mb-4" style={{fontFamily:'Space Grotesk'}}>I Nostri Servizi</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Offriamo una gamma completa di servizi medici specialistici per prenderci cura della tua salute</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 items-stretch auto-rows-fr" data-testid="home-services-grid">
            {homeServices.length > 0 ? homeServices.map((service) => (
              <Link key={service.id} to={`/servizi/${service.id}`} className="block group h-full" data-testid={`home-service-card-${service.id}`}>
                <Card className="h-full flex flex-col overflow-hidden hover:shadow-[var(--shadow-md)] hover:border-[rgba(35,138,146,.35)] transition-shadow transition-colors duration-200 cursor-pointer">
                  {service.immagine_url && (
                    <div className="h-40 overflow-hidden bg-[hsl(var(--secondary))]">
                      <img src={service.immagine_url} alt={service.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <Badge variant="secondary" className="mb-2 text-[11px]">{service.categoria}</Badge>
                    <h3 className="text-lg font-semibold text-[#1F2A37] mb-2 group-hover:text-[#238A92] transition-colors duration-200" style={{fontFamily:'Space Grotesk'}}>{service.nome}</h3>
                    {service.descrizione && <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">{service.descrizione}</p>}
                  </CardContent>
                </Card>
              </Link>
            )) : SPECIALTIES.map((spec) => {
              const Icon = spec.icon;
              return (
                <Card key={spec.name} className="group hover:shadow-[var(--shadow-md)] hover:border-[rgba(35,138,146,.35)] transition-shadow transition-colors duration-200 cursor-pointer">
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-xl ${spec.color} flex items-center justify-center mb-4`}><Icon size={24} /></div>
                    <h3 className="text-lg font-semibold text-[#1F2A37] mb-2" style={{fontFamily:'Space Grotesk'}}>{spec.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{spec.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link to="/servizi"><Button variant="outline" className="border-[#238A92] text-[#238A92] hover:bg-[#238A92] hover:text-white">Vedi Tutti i Servizi <ArrowRight size={16} className="ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      {/* Doctors */}
      <section className="section-mist py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2A37] mb-4" style={{fontFamily:'Space Grotesk'}}>La Nostra Equipe</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Professionisti qualificati al servizio della tua salute</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {doctors.map(doc => (
              <Card key={doc.id} className="overflow-hidden group hover:shadow-[var(--shadow-md)] transition-shadow duration-200">
                <div className="aspect-[3/4] overflow-hidden">
                  <img src={doc.foto_url || placeholderM} alt={`Dott. ${doc.cognome}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-[#1F2A37]" style={{fontFamily:'Space Grotesk'}}>Dott. {doc.nome} {doc.cognome}</h3>
                  <p className="text-sm text-[#238A92]">{doc.specializzazione}</p>
                  {bookingActive && (
                    <Link to="/prenota" className="mt-3 block">
                      <Button size="sm" variant="outline" className="w-full text-xs border-[#238A92] text-[#238A92] hover:bg-[#238A92] hover:text-white">Prenota con Dott. {doc.cognome}</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/equipe"><Button variant="outline" className="border-[#238A92] text-[#238A92] hover:bg-[#238A92] hover:text-white">Vedi Tutta l'Equipe <ArrowRight size={16} className="ml-2" /></Button></Link>
          </div>
        </div>
      </section>

      {/* Prenota ora */}
      {bookingActive && (
        <section className="py-12 sm:py-16" data-testid="home-book-now-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Link to="/prenota"><Button size="lg" className="bg-[#238A92] hover:bg-[#0F5E66] text-white px-8" data-testid="home-book-now-button"><Calendar size={18} className="mr-2" /> Prenota Ora</Button></Link>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="bg-[#238A92] text-white py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{fontFamily:'Space Grotesk'}}>Hai bisogno di informazioni?</h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">Il nostro team è a tua disposizione per qualsiasi domanda</p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-3"><Phone size={18} /> <span>02.6693281</span></div>
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-3"><MapPin size={18} /> <span>Via Settembrini 7, Milano</span></div>
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-3"><Clock size={18} /> <span>Lun-Ven 9-19 | Sab 9-13</span></div>
          </div>
        </div>
      </section>
    </div>
  );
}
